import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, expand, map, scan } from 'rxjs';

import { GraphService } from 'src/app/shared/lib/api/graph.service';
import {
  GET_INCOME_RECONCILIATION_DETAIL,
  GET_INCOME_RECONCILIATION_PAGE,
  GET_INCOME_RECONCILIATION_PENDING_COLLECTIONS,
  GET_INCOME_RECONCILIATION_ORDER_CANDIDATES,
} from '../graphql/income-reconciliation.queries';
import {
  IncomeReconciliationDetail,
  IncomeReconciliationDetailResponse,
  IncomeReconciliationPage,
  IncomeReconciliationPageInput,
  IncomeReconciliationPageResponse,
  IncomeReconciliationProgress,
  IncomeReconciliationPendingCollections,
  IncomeReconciliationPendingCollectionsResponse,
  IncomeReconciliationOrderCandidate,
  IncomeReconciliationOrderCandidatesInput,
  IncomeReconciliationOrderCandidatesPage,
  IncomeReconciliationOrderCandidatesProgress,
  IncomeReconciliationOrderCandidatesResponse,
} from '../models/income-reconciliation.models';

@Injectable({ providedIn: 'root' })
export class IncomeReconciliationService {
  private readonly graphQL = inject(GraphService);

  getPage(
    input: IncomeReconciliationPageInput = {},
  ): Observable<IncomeReconciliationPage> {
    return this.graphQL
      .queryGraphQL<IncomeReconciliationPageResponse>({
        query: GET_INCOME_RECONCILIATION_PAGE,
        variables: {
          input: { ...input, limit: input.limit ?? 50 },
        },
      })
      .pipe(map((response) => response.getConciliacionIngresosPage));
  }

  getDetail(transactionId: string): Observable<IncomeReconciliationDetail> {
    return this.graphQL
      .queryGraphQL<IncomeReconciliationDetailResponse>({
        query: GET_INCOME_RECONCILIATION_DETAIL,
        variables: { transactionId },
      })
      .pipe(map((response) => response.getConciliacionIngresoDetalle));
  }

  getPendingCollections(
    transactionId: string,
  ): Observable<IncomeReconciliationPendingCollections> {
    return this.graphQL
      .queryGraphQL<IncomeReconciliationPendingCollectionsResponse>({
        query: GET_INCOME_RECONCILIATION_PENDING_COLLECTIONS,
        variables: { transactionId },
      })
      .pipe(map((response) => response.getConciliacionIngresoCobrosPendientes));
  }

  getProgressively(
    input: IncomeReconciliationPageInput = {},
  ): Observable<IncomeReconciliationProgress> {
    const initialProgress: IncomeReconciliationProgress = {
      items: [],
      loadedPages: 0,
      hasMore: true,
    };

    return this.getPage({ ...input, nextToken: undefined }).pipe(
      expand((page) =>
        page.hasMore && page.nextToken
          ? this.getPage({ ...input, nextToken: page.nextToken })
          : EMPTY,
      ),
      scan(
        (progress, page) => ({
          items: [...progress.items, ...page.items],
          loadedPages: progress.loadedPages + 1,
          hasMore: page.hasMore,
        }),
        initialProgress,
      ),
    );
  }

  getOrderCandidatesPage(
    input: IncomeReconciliationOrderCandidatesInput,
  ): Observable<IncomeReconciliationOrderCandidatesPage> {
    return this.graphQL
      .queryGraphQL<IncomeReconciliationOrderCandidatesResponse>({
        query: GET_INCOME_RECONCILIATION_ORDER_CANDIDATES,
        variables: { input: { ...input, limit: input.limit ?? 100 } },
      })
      .pipe(map((response) => response.getIncomeReconciliationOrderCandidates));
  }

  getOrderCandidatesProgressively(
    input: IncomeReconciliationOrderCandidatesInput,
  ): Observable<IncomeReconciliationOrderCandidatesProgress> {
    const initialProgress: IncomeReconciliationOrderCandidatesProgress = {
      items: [],
      loadedPages: 0,
      hasMore: true,
    };

    return this.getOrderCandidatesPage({
      ...input,
      nextToken: undefined,
    }).pipe(
      expand((page) =>
        page.hasMore && page.nextToken
          ? this.getOrderCandidatesPage({
              ...input,
              nextToken: page.nextToken,
            })
          : EMPTY,
      ),
      scan(
        (progress, page) => ({
          items: this.mergeUniqueOrderCandidates(progress.items, page.items),
          loadedPages: progress.loadedPages + 1,
          hasMore: page.hasMore,
        }),
        initialProgress,
      ),
    );
  }

  private mergeUniqueOrderCandidates(
    current: IncomeReconciliationOrderCandidate[],
    incoming: IncomeReconciliationOrderCandidate[],
  ): IncomeReconciliationOrderCandidate[] {
    const itemsById = new Map(current.map((item) => [item.id, item]));
    incoming.forEach((item) => itemsById.set(item.id, item));
    return Array.from(itemsById.values());
  }
}
